import { z } from 'zod';
// CTSK-492 Phase 1a — GET /documents/search query contract
// (docs/document-search.md §4.2). Shared so Phase 1b's `useDocumentSearch`
// hook can validate client-side before firing and so the api route and
// the web client agree on limits.
//
// Bounds are the design doc's: q 2–200 chars, limit 1–100 (default 10),
// optional nest handle (slug OR uppercase code — same resolution rule as
// GET /nests/:handle), optional page ≥ 1 (default 1; the dropdown always
// sends page=1, the /documents?q= full-page fallback paginates).
//
// The api route maps the three documented failure modes onto their own
// error codes (QUERY_TOO_SHORT / QUERY_TOO_LONG / LIMIT_INVALID, see
// `documentSearchErrorCodeFor`) instead of the generic VALIDATION_ERROR,
// so the frontend can render "keep typing…" vs "shorten your query"
// without parsing Zod issue text.
export const DOCUMENT_SEARCH_QUERY_MIN = 2;
export const DOCUMENT_SEARCH_QUERY_MAX = 200;
export const DOCUMENT_SEARCH_LIMIT_DEFAULT = 10;
export const DOCUMENT_SEARCH_LIMIT_MAX = 100;
export const DocumentSearchQuerySchema = z
    .object({
    q: z
        .string()
        .trim()
        .min(DOCUMENT_SEARCH_QUERY_MIN, 'q must be at least 2 characters')
        .max(DOCUMENT_SEARCH_QUERY_MAX, 'q must be at most 200 characters'),
    limit: z.coerce
        .number()
        .int('limit must be an integer')
        .min(1, 'limit must be between 1 and 100')
        .max(DOCUMENT_SEARCH_LIMIT_MAX, 'limit must be between 1 and 100')
        .default(DOCUMENT_SEARCH_LIMIT_DEFAULT),
    nest: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'nest must be a nest slug or code')
        .optional(),
    page: z.coerce.number().int('page must be an integer').min(1, 'page must be >= 1').default(1),
})
    .strip();
/**
 * Map a Zod issue on the search query to the design-doc §4.4 error code.
 * Anything not covered falls back to VALIDATION_ERROR (the generic shape
 * every other route emits).
 */
export function documentSearchErrorCodeFor(issue) {
    const field = issue.path[0];
    if (field === 'q') {
        if (issue.code === 'too_small' || issue.code === 'invalid_type')
            return 'QUERY_TOO_SHORT';
        if (issue.code === 'too_big')
            return 'QUERY_TOO_LONG';
    }
    if (field === 'limit')
        return 'LIMIT_INVALID';
    return 'VALIDATION_ERROR';
}
//# sourceMappingURL=document-search.js.map